import { Button, Card } from '@parsina/framework';
import { CT } from '@parsina/framework/tokens';
import { CreditCard } from 'lucide-react';
import { C } from '@parsina/framework/clinical/src/constants/theme';
import { creditLevel, CREDIT_LEVEL_META } from '@parsina/framework/clinical/src/constants/credit';
import { fmtCoins } from '@parsina/framework/clinical/src/constants/creditPricing';
import { useTranslation } from '@parsina/framework/clinical/src/i18n/index';
import { useCreditPurchase } from '../../hooks/useCreditPurchase';

// ─────────────────────────────────────────────────────────────────────────────
// The «پارسه» balance, as a card body rather than a dot (finding 15, J-13).
//
// The phone had only the header's coloured dot: it says "something about your
// credit is not green" and nothing else — not the number, not what it means,
// and not what to do about it. The desktop rail has carried the figure, the
// warning and «شارژ اعتبار» all along.
//
// It renders on the cockpit only when there IS something to say — a `low` or
// `mid` balance. A healthy one stays a dot, because the reception desk is for
// the day's patients and a permanent billing card on it is noise. That is the
// same judgement the desktop rail makes by putting it below the fold.
// ─────────────────────────────────────────────────────────────────────────────

const MobileCreditCard = ({ balance, onTopUp }) => {
  const { t, formatNumber } = useTranslation();
  const { canBuy } = useCreditPurchase();
  const level = creditLevel(balance);

  // A healthy balance says nothing here — the header dot is the whole report.
  if (balance == null || (level !== 'low' && level !== 'mid')) return null;

  const urgent = level === 'low';
  const ink = urgent ? CT.dangerInk : CT.warnInk;

  return (
    <Card
      data-testid="reception-credit-card"
      style={{
        margin: '0.75rem 0.875rem 0',
        padding: '0.75rem 0.875rem',
        background: urgent ? CT.dangerSoft : CT.warnSoft,
        border: `1px solid ${urgent ? C.redLight : CT.warn}`,
        display: 'flex', alignItems: 'center', gap: '0.625rem',
      }}
    >
      <CreditCard size={18} aria-hidden style={{ color: ink, flexShrink: 0 }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: '0.75rem', fontWeight: 700, color: ink }}>
          {fmtCoins(balance, formatNumber)} {t('common.units.coin')}
        </div>
        <div style={{ fontSize: '0.6875rem', color: ink, opacity: 0.85, marginTop: '0.125rem' }}>
          {t(CREDIT_LEVEL_META[level].labelKey)}
        </div>
      </div>
      {/* Buying credit only where it is sold and this panel has a rail for its
          price (hooks/useCreditPurchase.js). The warning stays. */}
      {canBuy && (
        <Button
          variant={urgent ? 'default' : 'outline'}
          size="sm"
          className="shrink-0 min-h-11"
          data-testid="reception-credit-topup"
          onClick={onTopUp}
        >
          {t('credits.topUp')}
        </Button>
      )}
    </Card>
  );
};

export default MobileCreditCard;
